import { lazy, Suspense } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import PageTransition from './components/PageTransition';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import { Switch, Route } from 'wouter';

// Lazy-loaded foundation pages
const HomePage = lazy(() => import('./pages/Home.tsx'));
const AboutPage = lazy(() => import('./pages/About.tsx'));
const ProgrammesPage = lazy(() => import('./pages/Programmes.tsx'));
const ImpactPage = lazy(() => import('./pages/Impact.tsx'));
const PartnersPage = lazy(() => import('./pages/Partners.tsx'));
const GovernancePage = lazy(() => import('./pages/Governance.tsx'));
const DonatePage = lazy(() => import('./pages/Donate.tsx'));
const ContactPage = lazy(() => import('./pages/Contact.tsx'));
const ThankYouPage = lazy(() => import('./pages/ThankYou.tsx'));
const NotFound = lazy(() => import('./pages/not-found.tsx'));

/**
 * Main App component for DWB client.
 * Sets up routing, layout, theme context, and page transitions.
 *
 * @returns {JSX.Element} The root application component.
 */

export default function App() {
  return (
    // Provide theme context to all components
    <ThemeProvider>
      {/* Scroll to top on route changes */}
      <ScrollToTop />
      {/* Main layout wrapper */}
      <Layout>
        {/* Handles animated page transitions */}
        <PageTransition>
          {/* Suspense boundary for lazy-loaded routes */}
          <Suspense fallback={
              <div className="flex items-center justify-center min-h-[60vh]">
                <LoadingSpinner size="lg" message="Loading..." />
              </div>
            }>
            {/* Foundation route model */}
            <Switch>
              <Route path="/" component={HomePage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/programmes" component={ProgrammesPage} />
              <Route path="/impact" component={ImpactPage} />
              <Route path="/partners" component={PartnersPage} />
              <Route path="/governance" component={GovernancePage} />
              <Route path="/donate" component={DonatePage} />
              <Route path="/contact" component={ContactPage} />
              <Route path="/thank-you" component={ThankYouPage} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </PageTransition>
      </Layout>
    </ThemeProvider>
  );
}
